import { Pipe, PipeTransform } from '@angular/core';

import { DateHelperService } from '../../services/date-helper.service';

/**
 * When the route sorts itself, one rule has to answer two questions: which stops the sort moves,
 * and which stops still show their reorder controls. Those answers must be the same answer.
 *
 * Split them and they drift: a planner drags a stop that the sort silently owns, the drop looks
 * like it worked, and the next time edit puts it back where the clock says it belongs — an undo
 * arriving several actions after the action it undoes.
 */

/**
 * The moment a stop arrives, as a timestamp, or +Infinity when it has none to speak of.
 *
 * +Infinity rather than NaN so callers can compare and sort without special-casing: a stop
 * without a usable time is not "before" or "after" anything, it simply does not take part.
 */
export function arrivalTimestampOf(arrivalTime: unknown): number {
  // "Has a time" is decided by the helper the rest of this field already uses, never by a bare
  // parse. A new stop is seeded with a date and no clock time — `add()` writes 'YYYY-MM-DD' so
  // the input shows "--:--" — and `new Date('2026-09-10')` parses that happily, as UTC midnight.
  // A bare parse would therefore call an unscheduled stop scheduled: it would lose its reorder
  // controls AND be sorted to the start of the day, leaving the planner no way to place it.
  // extractTimeFromDateTime is the same rule deriveArrival and the required-time validator apply.
  if (typeof arrivalTime !== 'string' || !DateHelperService.extractTimeFromDateTime(arrivalTime)) {
    return Number.POSITIVE_INFINITY;
  }

  const parsed = new Date(arrivalTime);
  return isNaN(parsed.getTime()) ? Number.POSITIVE_INFINITY : parsed.getTime();
}

/**
 * Does this stop's own time decide where it sits?
 *
 * False for a stop that has no usable time — a freshly inserted one, most often. The sort leaves
 * those exactly where they are, so dragging them is the only thing that gives them a position,
 * and their controls stay.
 */
export function sortsOnTime(arrivalTime: unknown): boolean {
  return isFinite(arrivalTimestampOf(arrivalTime));
}

/**
 * Template-side access to the same rule.
 *
 * Takes the arrival time itself, never the stop object: a pure pipe re-runs when its input
 * REFERENCE changes, and Formly mutates a stop in place while the planner types. Passing the
 * object would leave the controls of a stop that just got a time on screen until something else
 * happened to rebuild the row.
 */
@Pipe({ name: 'sortsOnTime' })
export class SortsOnTimePipe implements PipeTransform {
  transform(arrivalTime: unknown): boolean {
    return sortsOnTime(arrivalTime);
  }
}
